/**
 * Recommendations Component
 *
 * Provides intelligent financial recommendations and advice based on company data.
 * Categories: Tax optimization, Capital structure, Liquidity, Growth, Risk management
 */

import { useState } from "react";
import {
  Lightbulb,
  TrendingUp,
  Shield,
  Wallet,
  PiggyBank,
  Scale,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Info,
  Building2,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { formatCurrency, formatPercent } from "@/lib/format";
import type {
  AnnualReport,
  AnnualReportSummary,
  CompanyWithDetails,
  BalanceSheet,
  IncomeStatement,
  KeyFigures,
} from "@/lib/api";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RecommendationPriority = "high" | "medium" | "low";
type RecommendationCategory =
  | "tax"
  | "capital"
  | "liquidity"
  | "growth"
  | "risk"
  | "industry";

interface Recommendation {
  id: string;
  category: RecommendationCategory;
  priority: RecommendationPriority;
  title: string;
  description: string;
  details?: string[];
  actionItems?: string[];
  potentialSavings?: string;
  legalRef?: string;
}

interface RecommendationsProps {
  report: AnnualReport | null;
  company: CompanyWithDetails;
  isLoading: boolean;
  availableReports?: AnnualReportSummary[];
  selectedReportId?: string;
  onSelectReport?: (reportId: string) => void;
}

// ---------------------------------------------------------------------------
// Category Configuration
// ---------------------------------------------------------------------------

const CATEGORY_CONFIG: Record<
  RecommendationCategory,
  { label: string; icon: React.ElementType; color: string; bgColor: string }
> = {
  tax: {
    label: "Skatteoptimering",
    icon: PiggyBank,
    color: "text-emerald-700",
    bgColor: "bg-emerald-50",
  },
  capital: {
    label: "Kapitalstruktur",
    icon: Scale,
    color: "text-blue-700",
    bgColor: "bg-blue-50",
  },
  liquidity: {
    label: "Likviditet",
    icon: Wallet,
    color: "text-cyan-700",
    bgColor: "bg-cyan-50",
  },
  growth: {
    label: "Tillväxt",
    icon: TrendingUp,
    color: "text-purple-700",
    bgColor: "bg-purple-50",
  },
  risk: {
    label: "Riskhantering",
    icon: Shield,
    color: "text-amber-700",
    bgColor: "bg-amber-50",
  },
  industry: {
    label: "Branschspecifikt",
    icon: Building2,
    color: "text-slate-700",
    bgColor: "bg-slate-50",
  },
};

const PRIORITY_CONFIG: Record<
  RecommendationPriority,
  { label: string; color: string; bgColor: string; borderColor: string }
> = {
  high: {
    label: "Hög prioritet",
    color: "text-red-700",
    bgColor: "bg-red-50",
    borderColor: "border-red-200",
  },
  medium: {
    label: "Medel prioritet",
    color: "text-amber-700",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
  },
  low: {
    label: "Låg prioritet",
    color: "text-blue-700",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
  },
};

// ---------------------------------------------------------------------------
// Company Profile Detection (simplified from FinancialHealth)
// ---------------------------------------------------------------------------

type CompanyProfile =
  | "holding"
  | "property"
  | "knowledge"
  | "service"
  | "trading"
  | "manufacturing"
  | "general";

function detectCompanyProfile(bs: BalanceSheet, is: IncomeStatement): CompanyProfile {
  const totalAssets = bs.assets.totalAssets;
  if (totalAssets <= 0) return "general";

  const buildings = bs.assets.fixedAssets.buildings || 0;
  const financialAssets = bs.assets.fixedAssets.financial || 0;
  const inventory = bs.assets.currentAssets.inventory || 0;
  const netSales = Math.abs(is.revenue.netSales);
  const personnelCosts = Math.abs(is.expenses.personnelCosts || 0);
  const goodsAndMaterials = Math.abs(is.expenses.goodsAndMaterials || 0);

  // Property company: >40% buildings
  if (buildings / totalAssets > 0.4) return "property";

  // Holding company: >50% financial assets, low revenue
  if (financialAssets / totalAssets > 0.5 && netSales < totalAssets * 0.1) return "holding";

  // Knowledge/Service company: High personnel costs relative to revenue
  if (netSales > 0 && personnelCosts / netSales > 0.4) {
    return personnelCosts / netSales > 0.5 ? "knowledge" : "service";
  }

  // Trading company: High inventory and goods costs
  if (inventory / totalAssets > 0.2 && goodsAndMaterials / netSales > 0.5) return "trading";

  // Manufacturing: High tangible assets and goods costs
  const tangibleAssets = bs.assets.fixedAssets.tangible || 0;
  if (tangibleAssets / totalAssets > 0.3 && goodsAndMaterials / netSales > 0.3) return "manufacturing";

  return "general";
}

// ---------------------------------------------------------------------------
// Recommendation Generation
// ---------------------------------------------------------------------------

function generateRecommendations(
  kf: KeyFigures,
  bs: BalanceSheet,
  is: IncomeStatement,
  userInput?: AnnualReport["userInput"],
  profile?: CompanyProfile
): Recommendation[] {
  const recommendations: Recommendation[] = [];
  const equity = bs.equityAndLiabilities.equity;
  const untaxedReserves = bs.equityAndLiabilities.untaxedReserves || 0;
  const totalAssets = bs.assets.totalAssets;
  const shortTermLiabilities = bs.equityAndLiabilities.shortTermLiabilities;
  const longTermLiabilities = bs.equityAndLiabilities.longTermLiabilities;
  const currentAssets = bs.assets.currentAssets.total;
  const cashAndBank = bs.assets.currentAssets.cashAndBank || 0;
  const netResult = is.netResult;
  const netSales = Math.abs(is.revenue.netSales);

  // Free equity available for dividends
  const freeEquity = equity.retainedEarnings + equity.currentYearResult;
  const proposedDividend = userInput?.proposedDividend || 0;

  // ═══════════════════════════════════════════════════════════════════
  // TAX OPTIMIZATION
  // ═══════════════════════════════════════════════════════════════════

  // High profit, low untaxed reserves → suggest periodization fund
  if (netResult > 100000 && untaxedReserves < netResult * 0.25) {
    const maxAllocation = netResult * 0.25; // 25% of profit
    recommendations.push({
      id: "tax-periodization-fund",
      category: "tax",
      priority: "high",
      title: "Överväg periodiseringsfond",
      description: `Med ett resultat på ${formatCurrency(netResult)} kan bolaget avsätta upp till ${formatCurrency(maxAllocation)} (25%) till periodiseringsfond och skjuta upp skatten i upp till 6 år.`,
      details: [
        "Periodiseringsfond minskar årets skattepliktiga resultat",
        "Fonden måste återföras inom 6 år",
        "Bra verktyg för att jämna ut resultat mellan år",
      ],
      actionItems: [
        "Boka avsättning till periodiseringsfond före bokslut",
        "Dokumentera beslutet i styrelseprotokoll",
      ],
      potentialSavings: `Upp till ${formatCurrency(maxAllocation * 0.206)} i uppskjuten skatt`,
      legalRef: "IL 30 kap",
    });
  }

  // High free equity, consider dividend timing
  if (freeEquity > 200000 && netResult > 50000) {
    const suggestedDividend = Math.min(freeEquity * 0.5, netResult);
    recommendations.push({
      id: "tax-dividend-planning",
      category: "tax",
      priority: "medium",
      title: "Planera utdelning strategiskt",
      description: `Med ${formatCurrency(freeEquity)} i fritt eget kapital finns möjlighet till skatteeffektiv utdelning på upp till ${formatCurrency(suggestedDividend)}. Tänk på 3:12-reglernas gränsbelopp.`,
      details: [
        "Utdelning inom gränsbeloppet beskattas med 20%",
        "Överskjutande utdelning beskattas som inkomst av tjänst",
        "Lönebaserat utrymme kan öka gränsbeloppet väsentligt",
        "Sparat utdelningsutrymme rullas vidare med ränta",
      ],
      actionItems: [
        "Beräkna årets gränsbelopp enligt 3:12-reglerna",
        "Överväg om utdelning eller lön är mest fördelaktigt",
        "Kontakta revisor för optimering",
      ],
      legalRef: "IL 57 kap (Fåmansföretagsregler)",
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // CAPITAL STRUCTURE
  // ═══════════════════════════════════════════════════════════════════

  // Critical: Equity below half share capital
  if (equity.total < equity.shareCapital / 2) {
    recommendations.push({
      id: "capital-control-balance",
      category: "capital",
      priority: "high",
      title: "Akut: Kontrollbalansräkning krävs",
      description: `Eget kapital (${formatCurrency(equity.total)}) understiger halva aktiekapitalet. Styrelsen måste agera omedelbart.`,
      details: [
        "Styrelsen ska genast upprätta kontrollbalansräkning",
        "Första kontrollstämma ska hållas inom 2 månader",
        "Om inte läkt inom 8 månader → likvidationsplikt",
      ],
      actionItems: [
        "Upprätta kontrollbalansräkning omedelbart",
        "Kalla till första kontrollstämma",
        "Överväg aktieägartillskott eller nyemission",
        "Konsultera jurist och revisor",
      ],
      legalRef: "ABL 25 kap 13-17§",
    });
  }

  // Low solidity warning
  if (kf.solidityPercent < 20 && kf.solidityPercent >= 0) {
    recommendations.push({
      id: "capital-low-solidity",
      category: "capital",
      priority: "high",
      title: "Stärk soliditeten",
      description: `Soliditeten är ${formatPercent(kf.solidityPercent)} vilket är under rekommenderade 20-30%. Detta ökar den finansiella risken.`,
      details: [
        "Låg soliditet innebär hög skuldsättning",
        "Kan påverka kreditvärdighet och lånekostnader",
        "Sårbart vid konjunkturnedgång",
      ],
      actionItems: [
        "Behåll mer av vinsten i bolaget",
        "Överväg aktieägartillskott",
        "Amortera på skulder istället för utdelning",
        "Se över kostnadsstrukturen",
      ],
    });
  }

  // Very high solidity - capital may be inefficient
  if (kf.solidityPercent > 60 && freeEquity > 500000) {
    recommendations.push({
      id: "capital-high-solidity",
      category: "capital",
      priority: "low",
      title: "Kapitalet kan användas effektivare",
      description: `Med ${formatPercent(kf.solidityPercent)} soliditet och ${formatCurrency(freeEquity)} i fritt eget kapital kan kapitalet potentiellt användas mer effektivt.`,
      details: [
        "Hög soliditet är positivt för stabilitet",
        "Men överkapitalisering kan ge låg avkastning",
        "Balansera säkerhet mot tillväxtmöjligheter",
      ],
      actionItems: [
        "Utvärdera investeringsmöjligheter",
        "Överväg expansion eller förvärv",
        "Analysera om extrautdelning är lämpligt",
      ],
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // LIQUIDITY
  // ═══════════════════════════════════════════════════════════════════

  const currentRatio = shortTermLiabilities > 0 ? currentAssets / shortTermLiabilities : 999;

  // Low liquidity
  if (currentRatio < 1.2 && currentRatio > 0) {
    recommendations.push({
      id: "liquidity-low-current",
      category: "liquidity",
      priority: "high",
      title: "Förbättra likviditeten",
      description: `Kassalikviditeten är ${currentRatio.toFixed(2)}x vilket är under rekommenderade 1.2x. Risk för betalningssvårigheter.`,
      details: [
        "Omsättningstillgångar täcker knappt kortfristiga skulder",
        "Kan leda till problem att betala leverantörer och löner",
        "Banker kan bli oroliga vid kreditförnyelse",
      ],
      actionItems: [
        "Se över kundfordringarnas betalningstider",
        "Förhandla längre betalningstid med leverantörer",
        "Överväg checkräkningskredit som buffert",
        "Fakturera snabbare och följ upp obetalda fakturor",
      ],
    });
  }

  // Very high cash - may be inefficient
  if (cashAndBank > totalAssets * 0.3 && cashAndBank > 500000) {
    recommendations.push({
      id: "liquidity-excess-cash",
      category: "liquidity",
      priority: "low",
      title: "Överväg placering av överlikviditet",
      description: `Bolaget har ${formatCurrency(cashAndBank)} i likvida medel (${formatPercent((cashAndBank / totalAssets) * 100)} av tillgångarna). Dessa pengar kan arbeta hårdare.`,
      details: [
        "Pengar på bankkonto ger minimal avkastning",
        "Överväg räntebärande placeringar med låg risk",
        "Behåll alltid en säkerhetsmarginal för oväntade utgifter",
      ],
      actionItems: [
        "Beräkna hur mycket likviditet som faktiskt behövs",
        "Undersök företagskonton med bättre ränta",
        "Överväg kortfristiga räntefonder",
        "Diskutera med bank om placeringsalternativ",
      ],
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // GROWTH RECOMMENDATIONS
  // ═══════════════════════════════════════════════════════════════════

  // Sustainable growth rate analysis
  if (netResult > 0 && equity.total > 0) {
    const roe = (netResult / equity.total) * 100;
    const retentionRate = proposedDividend > 0 ? 1 - (proposedDividend / netResult) : 1;
    const sustainableGrowth = roe * retentionRate;

    if (sustainableGrowth > 15) {
      recommendations.push({
        id: "growth-sustainable",
        category: "growth",
        priority: "medium",
        title: "Stark tillväxtkapacitet",
        description: `Bolaget har en hållbar tillväxttakt på ${formatPercent(sustainableGrowth)}. Detta är den tillväxt som kan finansieras utan extern kapital.`,
        details: [
          `Avkastning på eget kapital: ${formatPercent(roe)}`,
          `Bibehållen andel av vinsten: ${formatPercent(retentionRate * 100)}`,
          "Hög hållbar tillväxt indikerar stark position",
        ],
        actionItems: [
          "Utvärdera om tillväxtmöjligheter matchas av kapacitet",
          "Överväg expansion till nya marknader",
          "Investera i kompetens och kapacitet",
        ],
      });
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // RISK MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════

  // High receivables
  const receivables = bs.assets.currentAssets.receivables || 0;
  if (receivables > netSales * 0.2 && receivables > 200000) {
    recommendations.push({
      id: "risk-receivables",
      category: "risk",
      priority: "medium",
      title: "Höga kundfordringar - överväg kreditförsäkring",
      description: `Kundfordringarna är ${formatCurrency(receivables)} vilket motsvarar ${formatPercent((receivables / netSales) * 100)} av omsättningen. Överväg att skydda mot kundförluster.`,
      details: [
        "Höga kundfordringar binder kapital",
        "Risk för kundförluster vid kundens obestånd",
        "Kreditförsäkring kan minska risken",
      ],
      actionItems: [
        "Analysera kundfordringarnas åldersstruktur",
        "Implementera striktare kreditpolicy för nya kunder",
        "Undersök kreditförsäkring hos Atradius, Coface eller Euler Hermes",
        "Överväg factoring för snabbare betalning",
      ],
    });
  }

  // High debt ratio
  const debtRatio = totalAssets > 0
    ? ((longTermLiabilities + shortTermLiabilities) / totalAssets) * 100
    : 0;
  if (debtRatio > 70) {
    recommendations.push({
      id: "risk-high-debt",
      category: "risk",
      priority: "high",
      title: "Hög skuldsättning ökar risken",
      description: `Skuldsättningsgraden är ${formatPercent(debtRatio)}. Vid omsättningsnedgång kan det bli svårt att betala räntor och amorteringar.`,
      details: [
        "Hög skuldsättning ökar den finansiella risken",
        "Räntehöjningar påverkar resultatet kraftigt",
        "Kan begränsa möjligheten till nya krediter",
      ],
      actionItems: [
        "Prioritera amortering framför utdelning",
        "Förhandla om bättre lånevillkor",
        "Undvik nya skulder tills situationen förbättrats",
      ],
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // INDUSTRY-SPECIFIC RECOMMENDATIONS
  // ═══════════════════════════════════════════════════════════════════

  if (profile === "property") {
    recommendations.push({
      id: "industry-property-valuation",
      category: "industry",
      priority: "medium",
      title: "Fastighetsbolag: Överväg marknadsvärdering",
      description: "Under K2 bokförs fastigheter till anskaffningsvärde minus avskrivningar. Det verkliga marknadsvärdet kan vara väsentligt högre.",
      details: [
        "Bokfört värde avspeglar sällan marknadsvärdet",
        "Viktigt att känna till vid refinansiering",
        "Underlag för beslut om försäljning eller utveckling",
      ],
      actionItems: [
        "Inhämta extern fastighetsvärdering",
        "Jämför bokfört värde med marknadsvärde",
        "Använd vid bankförhandlingar för bättre belåningsgrad",
      ],
    });
  }

  if (profile === "knowledge" || profile === "service") {
    recommendations.push({
      id: "industry-knowledge-keyperson",
      category: "industry",
      priority: "medium",
      title: "Kunskapsbolag: Skydda nyckelpersoner",
      description: "I kunskapsföretag ligger värdet i medarbetarna. Överväg att skydda bolaget mot risken att förlora nyckelpersoner.",
      details: [
        "Nyckelpersonförsäkring ger ersättning vid dödsfall/sjukdom",
        "Kan finansiera rekrytering av ersättare",
        "Incitamentsprogram binder nyckelpersoner",
      ],
      actionItems: [
        "Identifiera verksamhetskritiska nyckelpersoner",
        "Undersök nyckelpersonförsäkring",
        "Överväg incitamentsprogram (bonus, syntetiska optioner)",
        "Dokumentera kritiska processer och kundrelationer",
      ],
    });
  }

  if (profile === "trading") {
    const inventory = bs.assets.currentAssets.inventory || 0;
    if (inventory > 0) {
      recommendations.push({
        id: "industry-trading-inventory",
        category: "industry",
        priority: "low",
        title: "Handelsbolag: Optimera lagerhållningen",
        description: `Varulagret är ${formatCurrency(inventory)}. Effektiv lagerstyrning kan frigöra kapital och minska risken för inkurans.`,
        details: [
          "Bundet kapital i lager kostar pengar",
          "Risk för inkurans vid låg omsättningshastighet",
          "Just-in-time kan minska lagerbindningen",
        ],
        actionItems: [
          "Analysera lagrets omsättningshastighet",
          "Identifiera långsamrörliga artiklar",
          "Överväg kampanjer för att sälja av gammalt lager",
          "Förbättra prognostisering av efterfrågan",
        ],
      });
    }
  }

  // Sort by priority
  const priorityOrder: RecommendationPriority[] = ["high", "medium", "low"];
  recommendations.sort(
    (a, b) => priorityOrder.indexOf(a.priority) - priorityOrder.indexOf(b.priority)
  );

  return recommendations;
}

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

function RecommendationCard({
  recommendation,
  isExpanded,
  onToggle,
}: {
  recommendation: Recommendation;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const category = CATEGORY_CONFIG[recommendation.category];
  const priority = PRIORITY_CONFIG[recommendation.priority];
  const CategoryIcon = category.icon;

  return (
    <div
      className={cn(
        "border rounded-xl overflow-hidden transition-all",
        priority.borderColor,
        isExpanded ? "shadow-md" : "shadow-sm hover:shadow-md"
      )}
    >
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-4 p-4 text-left hover:bg-gray-50 transition-colors"
      >
        {/* Icon */}
        <div
          className={cn(
            "flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center",
            category.bgColor
          )}
        >
          <CategoryIcon size={20} className={category.color} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={cn(
                "text-xs font-medium px-2 py-0.5 rounded-full",
                priority.bgColor,
                priority.color
              )}
            >
              {priority.label}
            </span>
            <span className="text-xs text-gray-500">{category.label}</span>
          </div>
          <h3 className="font-semibold text-gray-900">{recommendation.title}</h3>
          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
            {recommendation.description}
          </p>
        </div>

        {/* Expand icon */}
        <div className="flex-shrink-0 text-gray-400">
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-0 border-t border-gray-100">
          <div className="ml-14 space-y-4">
            {/* Details */}
            {recommendation.details && recommendation.details.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                  <Info size={14} />
                  Bakgrund
                </h4>
                <ul className="space-y-1">
                  {recommendation.details.map((detail, i) => (
                    <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-gray-400 mt-1.5">•</span>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action items */}
            {recommendation.actionItems && recommendation.actionItems.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                  <CheckCircle size={14} className="text-green-600" />
                  Rekommenderade åtgärder
                </h4>
                <ul className="space-y-1">
                  {recommendation.actionItems.map((item, i) => (
                    <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                      <ArrowRight size={14} className="text-primary-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Potential savings */}
            {recommendation.potentialSavings && (
              <div className="bg-emerald-50 rounded-lg p-3 flex items-center gap-2">
                <Sparkles size={16} className="text-emerald-600" />
                <span className="text-sm font-medium text-emerald-800">
                  {recommendation.potentialSavings}
                </span>
              </div>
            )}

            {/* Legal reference */}
            {recommendation.legalRef && (
              <p className="text-xs text-gray-400">Referens: {recommendation.legalRef}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-16">
      <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <Lightbulb size={32} className="text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Inga rekommendationer just nu
      </h3>
      <p className="text-gray-500 max-w-md mx-auto">
        Baserat på den tillgängliga datan har vi inga specifika rekommendationer.
        Detta kan betyda att bolaget redan är välskött!
      </p>
    </div>
  );
}

function CategoryFilter({
  categories,
  activeCategory,
  onSelect,
}: {
  categories: RecommendationCategory[];
  activeCategory: RecommendationCategory | "all";
  onSelect: (category: RecommendationCategory | "all") => void;
}) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 lg:mx-0 lg:px-0 scrollbar-hide">
      <button
        onClick={() => onSelect("all")}
        className={cn(
          "px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors",
          activeCategory === "all"
            ? "bg-gray-900 text-white"
            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
        )}
      >
        Alla
      </button>
      {categories.map((cat) => {
        const config = CATEGORY_CONFIG[cat];
        const Icon = config.icon;
        return (
          <button
            key={cat}
            onClick={() => onSelect(cat)}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors",
              activeCategory === cat
                ? "bg-gray-900 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            )}
          >
            <Icon size={14} />
            {config.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function Recommendations({
  report,
  company: _company,
  isLoading,
  availableReports,
  selectedReportId,
  onSelectReport,
}: RecommendationsProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<RecommendationCategory | "all">("all");

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!report?.parsedSieData) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-6">
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Lightbulb size={32} className="text-amber-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Ingen finansiell data tillgänglig
          </h3>
          <p className="text-gray-500 max-w-md mx-auto">
            För att få rekommendationer behöver du först ladda upp en SIE-fil och
            genomföra wizarden för att skapa en årsredovisning.
          </p>
        </div>
      </div>
    );
  }

  const { balanceSheet: bs, incomeStatement: is, keyFigures: kf } = report.parsedSieData;
  const profile = detectCompanyProfile(bs, is);
  const recommendations = generateRecommendations(kf, bs, is, report.userInput, profile);

  // Get unique categories
  const availableCategories = [...new Set(recommendations.map((r) => r.category))];

  // Filter recommendations
  const filteredRecommendations =
    activeCategory === "all"
      ? recommendations
      : recommendations.filter((r) => r.category === activeCategory);

  // Group by priority for summary
  const highPriority = recommendations.filter((r) => r.priority === "high").length;
  const mediumPriority = recommendations.filter((r) => r.priority === "medium").length;

  return (
    <div className="space-y-6">
      {/* Header with report selector */}
      {availableReports && availableReports.length > 1 && onSelectReport && (
        <div className="flex items-center justify-between">
          <div />
          <select
            value={selectedReportId || ""}
            onChange={(e) => onSelectReport(e.target.value)}
            className="input w-auto text-sm"
          >
            {availableReports.map((r) => (
              <option key={r.id} value={r.id}>
                {r.fiscalYearStart.slice(0, 4)}/{r.fiscalYearEnd.slice(0, 4)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{highPriority}</p>
              <p className="text-sm text-gray-500">Hög prioritet</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Info size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{mediumPriority}</p>
              <p className="text-sm text-gray-500">Medel prioritet</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Lightbulb size={20} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{recommendations.length}</p>
              <p className="text-sm text-gray-500">Totalt</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/25">
            <Lightbulb size={20} className="text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Rekommendationer</h2>
            <p className="text-xs text-gray-500">Strategiska råd baserat på ekonomisk data</p>
          </div>
        </div>

        {recommendations.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Category filter */}
            {availableCategories.length > 1 && (
              <div className="mb-6">
                <CategoryFilter
                  categories={availableCategories}
                  activeCategory={activeCategory}
                  onSelect={setActiveCategory}
                />
              </div>
            )}

            {/* Recommendation list */}
            <div className="space-y-4">
              {filteredRecommendations.map((rec) => (
                <RecommendationCard
                  key={rec.id}
                  recommendation={rec}
                  isExpanded={expandedId === rec.id}
                  onToggle={() =>
                    setExpandedId(expandedId === rec.id ? null : rec.id)
                  }
                />
              ))}
            </div>

            {filteredRecommendations.length === 0 && activeCategory !== "all" && (
              <p className="text-center text-gray-500 py-8">
                Inga rekommendationer i denna kategori.
              </p>
            )}
          </>
        )}

        {/* Disclaimer */}
        <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-xs text-gray-500">
            <strong>Obs:</strong> Dessa rekommendationer är generella och baserade på den
            finansiella datan. De ersätter inte professionell rådgivning från revisor,
            skattekonsult eller jurist. Kontakta alltid en expert innan du fattar viktiga
            beslut.
          </p>
        </div>
      </div>
    </div>
  );
}
